import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./loginPage.css";

import axios from "axios";

import { toast } from "../../components/Toast/toast.ts";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useAuthContext } from "../../context/AuthContext.tsx";

import AuthLayout from "../../components/ui/AuthLayout.tsx";
import AppButton from "../../components/ui/AppButton.tsx";
import AppInput from "../../components/ui/AppInput.tsx";
import PasswordInput from "../../components/ui/PasswordInput.tsx";

import { loginSchema, LoginFormData } from "./login.schema.ts";
import LightTooltip from "../../components/CustomTooltip.tsx";

export default function LoginPage() {
  const navigate = useNavigate();

  const { setAuth } = useAuthContext();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: "onChange",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  /* ----------------------------------------
     CHECK EXISTING LOGIN
  ---------------------------------------- */

  useEffect(() => {
    const storedToken = localStorage.getItem("token");

    if (storedToken) {
      setAuth(true);
      navigate("/home");
    }
  }, [navigate, setAuth]);

  /* ----------------------------------------
     LOGIN SUBMIT
  ---------------------------------------- */

  const onSubmit = async (data: LoginFormData) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/login/customer`,
        {
          email: data.email,
          password: data.password,
        },
      );

      const token = response?.data?.token;

      /* SAVE AUTH */
      localStorage.setItem("token", token);
      localStorage.setItem("auth", "true");

      /* USER ID */
      const userId = `${response?.data?.user?.firstName}_${response?.data?.user?._id}`;

      localStorage.setItem("user_id", userId);

      /* SET AUTH */
      setAuth(true);

      /* GTM EVENT */
      (window as any).dataLayer = (window as any).dataLayer || [];

      (window as any).dataLayer.push({
        event: "login",
        method: "email",
        custom_user_id: userId,
      });

      toast.success("Login successful");

      navigate("/home");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || err?.message || "Login failed",
      );
    }
  };

  return (
    <AuthLayout>
      <div className="flex flex-col gap-7">
        {/* HEADING */}
        <div>
          <h1 className="text-4xl font-light tracking-tight text-[rgb(var(--text-primary))]">
            Welcome back
          </h1>

          <p className="mt-3 page-subtitle">
            Sign in to continue to your dashboard
          </p>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          <AppInput
            label="Email"
            type="email"
            placeholder="Enter your email"
            autoComplete="username"
            disabled={isSubmitting}
            {...register("email")}
            error={errors.email?.message}
          />

          <PasswordInput
            label="Password"
            placeholder="Enter your password"
            autoComplete="current-password"
            disabled={isSubmitting}
            {...register("password")}
            error={errors.password?.message}
          />

          {/* FORGOT PASSWORD */}
          <div className="flex flex-col items-start gap-2">
            <button
              type="button"
              onClick={() => navigate("/forgot-pwd")}
              className="
                text-[13px]
                text-[rgb(var(--text-muted))]
                hover:text-[rgb(var(--color-gold-2))]
                transition-colors
              "
            >
              Forgot password?
            </button>

            <LightTooltip arrow placement="top" title="If you forget both your email and password, you can contact the support team, available 24/7, by phone at (253) 220-7200 or via email at help@myliveeye.com">
              <button
                type="button"
                className="
                text-[13px]
                text-[rgb(var(--text-muted))]
                hover:text-[rgb(var(--color-gold-2))]
                transition-colors
              "
              >
                Forgot both Email & Password?
              </button>
            </LightTooltip>
          </div>

          {/* SUBMIT */}
          <AppButton
            type="submit"
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? "Signing In..." : "Sign In"}
          </AppButton>
        </form>

        {/* SUPPORT */}
        <div className="pt-1">
          <p className="text-[12px] leading-6 text-[rgb(var(--text-tertiary))]">
            Need help? Contact support team available 24/7.
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}
